import { AppMessagesContext, Localization, PersistenceObjectType, PersistenceScope, SessionContext, SettingsManager, ViamapPersistenceLayer, actionSetInfoMessage } from "@viamap/viamap2-common";
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { FeatureLayer, getFeatureLayerGroupTranslation } from 'src/managers/WmsLayerFunc';
import { ProtectedFeature } from './ProtectedFeature';
import { Feature } from 'src/states/ApplicationStateFeatures';
import { ApplicationStateContext } from 'src/states/ApplicationState';
import { LayerInfo } from 'src/common/managers/Types';
import { v4 } from 'uuid';
import { MitStorage } from 'src/managers/MitStorage';
import { GlassCheckbox, SmallIconBtn, VLine } from './MitGlassComponents';
import { ConfirmationDialog } from 'src/components/ConfirmationDialog';
import { GlassButton } from './GlassButtons';
import { GroupedLayerSearch } from "./GroupedLayerSearch";
import { message } from "./Message";
import { ActionTracker } from "src/common/managers/ActionTracker";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { MapitStateContext } from "src/states/MapitState";


type Props<T> = {
   open: boolean;
   callBackonSettingsFeatureLayer(key: string, show: boolean): unknown;
   addedAttributes: number;
   backgroundLayers: { [name: string]: string };
   featureLayers: { [name: string]: T };
   selectedBackgroundLayerKey: string;
   selectedFeatureLayerKeys: string[];
   customFeatureLayers: T[];
   editFeatureAvailable: boolean;
   recentLayers: { key: string, layerkeys: string[] }[]
   onCustomFeatureLayerToggle: (customFeatureLayers: LayerInfo) => void,
   callBackOnSelectBackgroundLayer: (key: string) => void;
   callBackRecentRecentEntry: (key: string) => void;
   callBackOnSelectFeatureLayer: (key: string, selected: boolean, layer?: { key: string, layer?: FeatureLayer }[], history?: boolean) => void;
   callbackToConvertCustomFeatureLayerToNormal: (featureLayer: any) => void;
   callBackOnFavLayerEditor: (group: CustomGroup) => void;

};

export function GroupedLayerControl(props: Props<any>) {
   const isReady = useRef(false)
   const { state } = useContext(SessionContext)
   const [myCustomGroups, setMyCustomGroups] = useState<CustomGroup[]>([])
   const [onlineCustomGroups, _setOnlineCustomGroups] = useState<CustomGroup[]>([])

   const { hasAccessToFeature } = useContext(ApplicationStateContext)

   useEffect(() => {
      if (isReady.current && SettingsManager.getSystemSetting("SaveOpendedFeatureLayers", true)) {
         const individualLayers = props.selectedFeatureLayerKeys.filter(key => !key.includes("\\"));
         const groupUUIDs = getActiveGroupUUIDs(props.selectedFeatureLayerKeys);

         SaveSelectedLayers(individualLayers, groupUUIDs);
      }
      if (isReady.current && !SettingsManager.getSystemSetting("SaveOpendedFeatureLayers", true)) {
         SaveSelectedLayers([], []);
      }
      isReady.current = true
   }, [props.selectedFeatureLayerKeys])

   const CFLGFileName = "CutomFeatureLayersGroups.json";
   useEffect(() => {
      if (!hasAccessToFeature(Feature.FavoriteBackgroundLayers)) {
         return;
      }

      const abort = new AbortController()
      let persit = new ViamapPersistenceLayer(SettingsManager.getSystemSetting("viamapStoreS3Bucket"))
      persit.getObject(PersistenceScope.User, PersistenceObjectType.CustomLayers, state.customerRef, state.userRef, CFLGFileName)
         .then((a) => {
            if (abort.signal.aborted) return;
            let str = a.Body.toString("utf-8")
            setMyCustomGroups(() => [...JSON.parse(str)].map((a) => ({ ...a, online: false }) as CustomGroup))
         }).catch(() => {
            return null //Do Nothing
         })
      persit.getListing(PersistenceScope.Customer, PersistenceObjectType.CustomLayers, state.customerRef, state.userRef).then(async (a) => {
         if (abort.signal.aborted) return;
         let x = (await Promise.all(a.map(async (a) => {
            return await persit.getObjectS3(a.Key).then((b) => {
               if (abort.signal.aborted) return;
               let str = b.Body.toString("utf-8")
               let obj = JSON.parse(str)
               if (validateCustomGroup(obj)) {
                  return CustomGroupVersion(obj)
               }
               return null
            }).catch((_e) => {
               console.error("Error fetching online custom group", _e);
               return null
            })
         }))).filter((a) => a)
         _setOnlineCustomGroups(x.map((a) => ({ ...a, online: true } as CustomGroup)))
      }).catch(() => {
         return null //Do Nothing
      })
      return () => abort.abort()
   }, [state.userRef, state.customerRef])

   function saveCustomFeatureLayerGroups(myCustomGroups: CustomGroup[]) {
      if (!hasAccessToFeature(Feature.FavoriteBackgroundLayers)) {
         return;
      }
      let persist = new ViamapPersistenceLayer(SettingsManager.getSystemSetting("viamapStoreS3Bucket"))
      persist.uploadObject(PersistenceScope.User, PersistenceObjectType.CustomLayers, state.customerRef, state.userRef, JSON.stringify(myCustomGroups), CFLGFileName)
   }

   function addOnlineCustomGroup(customGroup: CustomGroup) {
      if (!hasAccessToFeature(Feature.FavoriteBackgroundLayers)) {
         return;
      }
      let persist = new ViamapPersistenceLayer(SettingsManager.getSystemSetting("viamapStoreS3Bucket"))
      persist.uploadObject(PersistenceScope.Customer, PersistenceObjectType.CustomLayers, state.customerRef, state.userRef, JSON.stringify({ ...customGroup, owner: state.userRef, online: true }), customGroup.uuid)
         .then(() => {
            setMyCustomGroups((a) => {
               let x = a.filter((b) => b.uuid !== customGroup.uuid)
               saveCustomFeatureLayerGroups(x)
               return x
            })
            _setOnlineCustomGroups((a) => [...a, { ...customGroup, owner: state.userRef, online: true }])
         }).catch((e) => {
            console.error("Error adding online custom group", e);
         })
   }

   function removeOnlineCustomGroup(customGroup: CustomGroup) {
      let persist = new ViamapPersistenceLayer(SettingsManager.getSystemSetting("viamapStoreS3Bucket"))
      persist.deleteObject(PersistenceScope.Customer, PersistenceObjectType.CustomLayers, state.customerRef, state.userRef, customGroup.uuid)
         .then(() => {
            let userRef = state.userRef
            setMyCustomGroups((a) => {
               if (customGroup.owner == userRef) {
                  let x = [...a, { ...customGroup, online: false }]
                  saveCustomFeatureLayerGroups(x)
                  return x
               } else {
                  return a.filter((b) => b.uuid !== customGroup.uuid)
               }
            })
            _setOnlineCustomGroups((a) => a.filter((b) => b.uuid !== customGroup.uuid))
         }).catch((e) => {
            console.error("Error removing online custom group", e);
         })
   }

   function reduceCustomGroups(action: string, group: CustomGroup) {
      switch (action) {
         case "Online":
            addOnlineCustomGroup(group);
            break;
         case "Offline":
            removeOnlineCustomGroup(group);
            break;
      };
   }

   function GroupReducer(action, value: { uuid: string } & Partial<CustomGroup>) {
      if (value.online) {
         message.error(Localization.getText("Make group offline before editing it"))
         return;
      }
      let X = myCustomGroups
      switch (action) {
         case "Remove":
            X = X.filter((a) => a.uuid !== value.uuid)
            break;
         case "Rename":
            X = X.map((a) => { return a.uuid === value.uuid ? { ...a, title: value.title || "" } : a })
            break;
         case "Add":
            X = [...(X.filter((a) => a.uuid !== value.uuid)), (value as CustomGroup)]
      }
      setMyCustomGroups([...X])
      saveCustomFeatureLayerGroups([...X])
   }

   function onBackgroundLayerChange(newKey: string) {
      props.callBackOnSelectBackgroundLayer(newKey);
   }

   function onFeatureLayerChange(key: string, checked: boolean, layers?: { key: string, layer?: FeatureLayer }[], history = true) {
      props.callBackOnSelectFeatureLayer(key, checked, layers, history);
      if (checked) {
         // addRecentLayer(key, layer);
      }
   }

   function onSettingClick(key: string, show: boolean) {
      props.callBackonSettingsFeatureLayer(key, show);
   }

   function turnAllLayersOff() {
      props.customFeatureLayers.forEach((val) => {
         if (val?.visible) {
            props.onCustomFeatureLayerToggle(val)
         }
      })

      props.selectedFeatureLayerKeys.forEach(key => {
         props.callBackOnSelectFeatureLayer(key, false);
      });
   }

   // MARK: Render Group

   return (

      <div className='Wrap' >
         <div className='Head FeaturelayerHead'>
            <GlassButton disabled={!(props.selectedFeatureLayerKeys.length || props.customFeatureLayers.filter(a => a.visible).length)} onClick={() => turnAllLayersOff()} >{Localization.getText("Reset")}</GlassButton>
            <GroupedLayerSearch
               open={props.open}
               callBackOnSelectFeatureLayer={props.callBackOnSelectFeatureLayer}
               customFeatureLayers={props.customFeatureLayers}
               onCustomFeatureLayerToggle={props.onCustomFeatureLayerToggle}
               featureLayers={props.featureLayers}
               selectedFeatureLayerKeys={props.selectedFeatureLayerKeys}
               myCustomGroups={myCustomGroups}
            />
         </div>
         <VLine color={"#dddd"} />
         <div className='Body'>
            {/*
            <BackgroundLayerGrouped
               backgroundLayers={props.backgroundLayers}
               onBackgroundLayerChange={(a) => onBackgroundLayerChange(a)}
               selectedBackgroundLayerKey={props.selectedBackgroundLayerKey}
            />
            */}
            <ProtectedFeature feature={Feature.FavoriteBackgroundLayers} contentsIfNoAccess={<></>} >
               <RecentFeatureLayersList
                  myRecentLayers={props.recentLayers}
                  selectedFeatureLayerKeys={props.selectedFeatureLayerKeys}
                  callbackChangeFeatureLayers={props.callBackOnSelectFeatureLayer}
                  onSettingsClick={(a, b) => onSettingClick(a, b)}
                  removeRecent={(a) => props.callBackRecentRecentEntry(a)}
               />
            </ProtectedFeature>
            <ProtectedFeature feature={Feature.FavoriteBackgroundLayers} contentsIfNoAccess={<></>} >
               <CustomFeatureLayersGroups
                  layers={props.featureLayers}
                  groupedLayer={[...onlineCustomGroups, ...myCustomGroups]}
                  onGroupChange={GroupReducer}
                  backgroundLayers={props.backgroundLayers}
                  onChange={(a, b, c?, d?) => onFeatureLayerChange(a, b, c, d)}
                  selectedFeatureLayerKeys={props.selectedFeatureLayerKeys}
                  onBackgroundLayerChange={(a) => onBackgroundLayerChange(a)}
                  selectedBackgroundLayerKey={props.selectedBackgroundLayerKey}
                  callBackEdit={group => props.callBackOnFavLayerEditor(group)}
                  callBack={(action: string, group: CustomGroup) => reduceCustomGroups(action, group)}
               />
            </ProtectedFeature>
            <SavedMapLayerGroup
               customFeatureLayers={props.customFeatureLayers}
               editFeatureAvailable={props.editFeatureAvailable}
               callbackToConvertCustomFeatureLayerToNormal={(a) => props.callbackToConvertCustomFeatureLayerToNormal(a)}
               onCustomFeatureLayerToggle={(a) => props.onCustomFeatureLayerToggle(a)}
            />
            <FeatureLayers
               layers={props.featureLayers}
               onChange={(a, b, c, d) => onFeatureLayerChange(a, b, c, d)}
               settingClick={(a, b) => onSettingClick(a, b)}
               showSettings={true}
               selectedFeatureLayerKeys={props.selectedFeatureLayerKeys}
            />
         </div>
      </div>
   );
}




type PropsCustomFeatureLayersGroups = {
   layers: { [key: string]: any },
   groupedLayer: CustomGroup[]
   backgroundLayers: { [key: string]: string },
   onChange: (a, b, c?, d?) => any,
   onGroupChange(action, value),
   selectedFeatureLayerKeys: string[],
   onBackgroundLayerChange: (a) => any
   selectedBackgroundLayerKey: string,
   callBack: (act: string, group: CustomGroup) => void
   callBackEdit: (group: CustomGroup) => void
}
export type CustomGroup = {
   uuid: string,
   title: string,
   bgLayer: string,
   layers: string[]
   online?: boolean,
   owner?: string,
   customLayers: { layerVariability: number, datasetname: string, group: string[] }[]
   layerSpecs: { [key: string]: FeatureLayer }
}



function CustomFeatureLayersGroups(props: PropsCustomFeatureLayersGroups) {
   let { dispatch: appMessageDispatch } = useContext(AppMessagesContext)
   let [collapsed, setCollapsed] = useState(false)


   // if (!(props.groupedLayer?.length)) return null

   function onRemove(uuid: string) {
      props.groupedLayer.find((a) => a.uuid === uuid)?.layers.forEach((a) => {
         props.onChange(a, false, [{ key: "\\" + uuid + "\\" + a }], false)
      })
      ActionTracker.track("LayerControl", "CustomFeatureLayerGroup", "Remove", { groupID: props.groupedLayer.find((a) => a.uuid === uuid)?.title || "" });
      props.onGroupChange("Remove", { uuid })
   }

   function onEdit(obj) {
      props.onGroupChange("Rename", obj)
   }

   function onAdd() {
      function checkIfAnyActive() {
         return props.selectedFeatureLayerKeys?.length ||
            Object.values(props.layers).filter((a) => (a.layerVariability == 1 && a.visible)).length
      }

      if (!(checkIfAnyActive())) {
         appMessageDispatch(actionSetInfoMessage(Localization.getText("Please activate layers to be included, before adding a group")))
         return
      }
      let layerSpecs = {}
      let uuid = v4()
      props.selectedFeatureLayerKeys.filter((a) => !a.includes("\\")).forEach((a) => {
         layerSpecs = { ...layerSpecs, [a]: props.layers[a].copy(uuid) }
      })
      const NewGroup: CustomGroup = {
         uuid: uuid,
         title: "",
         bgLayer: "",
         layers: props.selectedFeatureLayerKeys.filter((a) => !a.includes("\\")),
         customLayers: [], // TODO:
         layerSpecs: layerSpecs
      }
      props.onChange(NewGroup.title, false,
         props.selectedFeatureLayerKeys.filter((a) => !a.includes("\\")).map((a) => ({ key: a, layer: props.layers[a] }))
         , false)
      props.onChange(NewGroup.title, true,
         NewGroup.layers.map((a) => ({ key: "\\" + NewGroup.uuid + "\\" + a, layer: NewGroup.layerSpecs?.[a] }))
         , false)


      setCollapsed(false)
      ActionTracker.track("LayerControl", "CustomFeatureLayerGroup", "Add", { groupID: NewGroup.title });
      props.onGroupChange("Add", NewGroup)
   }

   return (
      <div className='GroupWrap'
         onDragOver={(e) => { e.preventDefault() }}
         onDrop={async (e) => {
            e.preventDefault()
            let files = [...e.dataTransfer.files]
            files.map(async (a) => {
               let obj = JSON.parse(await a.text())
               if (validateCustomGroup(obj)) {
                  ActionTracker.track("LayerControl", "CustomFeatureLayerGroup", "AddFromFile", { groupID: obj.title });
                  props.onGroupChange("Add", CustomGroupVersion(obj))
               }
            })
         }}
      >
         <div onClick={() => setCollapsed((a) => (!a))} className='GroupHead'>
            <MIBIcon icon="favoriteLayers" color='#000' width={"1em"} height={"0.2em"} />
            <label>{Localization.getText("Favorite layers")}</label>
            <SmallIconBtn title={Localization.getText("Add Layer")} onClick={(e) => { onAdd(); e.stopPropagation() }} className={'shownOnHoverParent'} >
               <MIBIcon icon="addFavoriteLayer" />
            </SmallIconBtn>
            <SmallIconBtn >
               <MIBIcon icon="expandLayer" className='transformer' style={{ transform: "Rotate(" + (!collapsed ? "90deg" : "0deg") + ")" }} />
            </SmallIconBtn>
         </div>
         <div className='layerInGroup' >
            {!collapsed && props.groupedLayer.map((a) => {
               return (
                  <Fragment key={a.uuid} >
                     <SingleCustomLayer group={a} remove={onRemove} callBack={props.callBack} edit={onEdit} openEditor={props.callBackEdit} featureLayers={props.layers} selectBackgroundLayer={props.onBackgroundLayerChange} selectedLayers={props.selectedFeatureLayerKeys} onChange={props.onChange} />
                  </Fragment>
               )
            })}
         </div>
      </div>
   )
}

function SingleCustomLayer(props: { group: CustomGroup, featureLayers: any, selectBackgroundLayer: (a) => void, selectedLayers: string[], openEditor: (group: CustomGroup) => void, onChange: (a, b, c?, d?) => any, remove, edit, callBack: (act: string, group: CustomGroup) => void }) {
   const [showShareDialog, setShowShareDialog] = useState(false);
   const { state } = useContext(SessionContext)
   const { hasAccessToFeature } = useContext(ApplicationStateContext)
   let [edit, setEdit] = useState(!(props.group.title))

   let layersInGroup = props.group.layers.filter((a) => props.group.layerSpecs || props.featureLayers[a])
   let activeInGroup = layersInGroup.filter((a) => props.selectedLayers.includes("\\" + props.group.uuid + "\\" + a))

   function toggleGroup(e) {
      e.preventDefault()
      e.stopPropagation();
      if (activeInGroup.length) {
         ActionTracker.track("LayerControl", "CustomFeatureLayerGroup", "ToggleOff", { groupID: props.group.title, groupLayers: layersInGroup.map((a) => a) });
         props.onChange(props.group.title, false,
            layersInGroup.map((a) => ({ key: "\\" + props.group.uuid + "\\" + a, layer: props.group.layerSpecs?.[a] }))
            , false)
      } else {
         ActionTracker.track("LayerControl", "CustomFeatureLayerGroup", "ToggleOn", { groupID: props.group.title, groupLayers: layersInGroup.map((a) => a) });
         props.selectBackgroundLayer(props.group.bgLayer);
         props.onChange(props.group.title, true,
            layersInGroup.map((a) => ({ key: "\\" + props.group.uuid + "\\" + a, layer: props.group.layerSpecs?.[a] }))
            , false)
      }
   }

   function Blur(val) {
      if (val) {
         ActionTracker.track("LayerControl", "CustomFeatureLayerGroup", "Rename", { groupID: props.group.title });
         props.edit({ ...props.group, title: val })
         setEdit(false)
      }
   }

   function isOnline() {
      return props.group.online;
   }

   function isAdmin() {
      return hasAccessToFeature(Feature.GlobalAdministrator) ||
         (hasAccessToFeature(Feature.CustomerAdministrator) && props.group.owner?.includes(state.customerRef))
   }

   function isMine() {
      return props.group.owner === state.userRef;
   }

   function online() {
      ActionTracker.track("LayerControl", "CustomFeatureLayerGroup", "ShareOnline", { groupID: props.group.title });
      props.callBack("Online", props.group)
   }

   function offline() {
      ActionTracker.track("LayerControl", "CustomFeatureLayerGroup", "ShareOffline", { groupID: props.group.title });
      props.callBack("Offline", props.group)
   }

   function download() {
      ActionTracker.track("LayerControl", "CustomFeatureLayerGroup", "Download", { groupID: props.group.title });
      let data = JSON.stringify(props.group)
      downloadJson(data, props.group.title + ".json")
   }

   return (<>
      {/* // <div className='GroupWrap'> */}
      {/* <div className='GroupHead'> */}
      <div className='layer'>

         <GlassCheckbox onClick={toggleGroup} checked={!!activeInGroup.length} indeterminate={!!(activeInGroup.length && activeInGroup.length < layersInGroup.length || false)} />
         {isOnline() ? <MIBIcon icon="publishedLayer" /> : null}
         {!edit ? <span onDoubleClick={() => { if (isOnline()) { message.error(Localization.getText("Cannot edit online group")); return }; setEdit(true) }} title={isOnline() ? props.group.owner ?? Localization.getText("Unknown") : Localization.getText("2xClick to Edit Name")} >{props.group.title}</span> :
            <input autoFocus={true} placeholder={Localization.getText("Enter name")} defaultValue={props.group.title} onBlur={((e) => { Blur(e.target.value) })} />
         }
         <SmallIconBtn title={Localization.getText("See layers in group")} onClick={(e) => { props.openEditor(props.group); e.stopPropagation() }} className='shownOnHoverParent'>
            <MIBIcon icon="layerInfo" />
         </SmallIconBtn>
         {!isOnline() && hasAccessToFeature(Feature.ShareFavoriteBackgroundLayers) ? (
            <>
               <SmallIconBtn
                  title={Localization.getText("Share with company")}
                  onClick={() => setShowShareDialog(true)}
                  className='shownOnHoverParent'
               >
                  <MIBIcon icon="publishLayer" />
               </SmallIconBtn>
               {showShareDialog && (
                  <ConfirmationDialog
                     showWindow={1}
                     header={"Share with company"}
                     onSubmit={() => {
                        setShowShareDialog(false);
                        online();
                     }}
                     onCancel={() => setShowShareDialog(false)}
                  >
                     {Localization.getText("Share with Company Confirmation")}
                  </ConfirmationDialog>
               )}
            </>
         ) : null}
         <SmallIconBtn title={Localization.getText("Download layer")} onClick={() => (download())} className='shownOnHoverParent'>
            <MIBIcon icon="downloadLayerSettings" />
         </SmallIconBtn>
         {isOnline() && (isMine() || isAdmin()) ? <SmallIconBtn title={Localization.getText("Stop sharing layer")} onClick={() => (offline())} className='shownOnHoverParent'>
            <MIBIcon icon="unpublishLayer" />
         </SmallIconBtn> : null}
         {!isOnline() ? <SmallIconBtn title={Localization.getText("Delete layer")} onClick={() => (props.remove(props.group.uuid))} className='shownOnHoverParent'>
            {/* <BsPenFill onClick={() => {props.edit(props.group.uuid)}} /> */}
            <MIBIcon icon="removeLayer" />
         </SmallIconBtn> : null}
         {/* <span className='mit-transformer' style={{float:"right", transform:"Rotate("+(!collapsed?"90deg":"0deg")+")"}}><BsChevronRight /></span> */}

         {/* </div></div> */}
         {/* {!collapsed ? <div className='layerInGroup' >
      {props.group.layers.map((key) => {
         return (
         <div key={key} className='layer'>
         <MitGlassCheckbox variant='dark' onClick={(evt, val) => props.onChange(key, val)} checked={activeInGroup.includes(key)} >{(props.featureLayers[key] as any)?.translationTable ? Localization.getTextSpecificTable(key, (props.featureLayers[key] as any).translationTable) : key}</MitGlassCheckbox>
         </div>
         )
      })}
      </div>
      : null} */}

      </div>
   </>)
}

type PropsRecentFeatureLayers = {
   myRecentLayers: any[],
   // backgroundLayers: {[key:string]:string},
   selectedFeatureLayerKeys: string[],
   callbackChangeFeatureLayers: (a, b, c, d) => void,
   onSettingsClick: (a, b) => void,

   removeRecent: (a: string) => void,
}

function RecentFeatureLayersList(props: PropsRecentFeatureLayers) {
   let [collapsed, setCollapsed] = useState(false)
   let { state: mapitState } = useContext(MapitStateContext);

   // if (!(props.groupedLayer?.length)) return null

   return (
      <div className='GroupWrap'>
         <div onClick={() => setCollapsed((a) => (!a))} className='GroupHead'>
            <MIBIcon icon="recentLayers" color='#000' width={"1em"} height={"0.2em"} />
            <label>{Localization.getText("Recent layers")}</label>
            <SmallIconBtn >
               <MIBIcon icon="expandLayer" className='transformer' style={{ transform: "Rotate(" + (!collapsed ? "90deg" : "0deg") + ")" }} />
            </SmallIconBtn>
         </div>
         <div className='layerInGroup' >
            {!collapsed && props.myRecentLayers.map((a) => {
               const isGroup = (a.layerkeys.length > 1 || a.layerkeys[0]?.startsWith("\\"));
               const isChecked = (a.layerkeys as string[]).every(element => {
                  return props.selectedFeatureLayerKeys.includes(element)
               });
               const indeterminate = (a.layerkeys as string[]).some(element => {
                  return props.selectedFeatureLayerKeys.includes(element)
               });

               let keyS = a.layerkeys.length === 1 ? a.layerkeys[0] : null;


               return (
                  <div key={a.key} className='layer'>
                     <GlassCheckbox onClick={(evt, val) => props.callbackChangeFeatureLayers(a.key, val, a.layerkeys.map((a) => ({ key: a })), false)} indeterminate={!isChecked && indeterminate} checked={isChecked || indeterminate} >
                        {isGroup ? <b>{a.key}</b> : a.key}
                     </GlassCheckbox>
                     {keyS && (mapitState.featureLayers[keyS] as any)?.layer?.infoLink ? <LayerInfoLink layerId={keyS} href={(mapitState.featureLayers[keyS] as any)?.layer?.infoLink || ""} /> : null}
                     <SettingsButton onClick={() => {
                        a.layerkeys.forEach((key) => {
                           props.onSettingsClick?.(key, true)
                        });
                     }} />
                     <SmallIconBtn onClick={() => props.removeRecent(a.key)} className='shownOnHoverParent' title={Localization.getText("Delete layer")}>
                        <MIBIcon icon="removeLayer" />
                     </SmallIconBtn>

                  </div>
               )
            })}
         </div>
      </div>
   )
}

type FastLagGroup<T> = {
   customFeatureLayers?: T[],
   callbackToConvertCustomFeatureLayerToNormal: (layer: T) => void,
   onCustomFeatureLayerToggle: (layer: T) => void,
   editFeatureAvailable?: boolean,
}

function FasteLagInGroup(key: string[], layers: LayerInfo[]): LayerInfo[] {
   return layers.filter((layer) => key.every((a, idx) => a == layer.group?.[idx]))
}

function FasteLagAtLevel(key: string[], layers: LayerInfo[]): LayerInfo[] {
   return layers.filter((layer) => layer.group?.length === key.length && key.every((a, idx) => a == layer.group?.[idx]))
}

function uniqueSubGroup(layers: LayerInfo[], cur: string[]): string[][] {
   const SEPERATOR = "⛄"
   let curLeng = cur.length || 1
   let x = Array.from(new Set(layers.map((layer) => {
      let splitted = [...layer.group!]
      if ((splitted?.length) > curLeng) {
         return splitted.splice(0, curLeng + 1).join(SEPERATOR)
      }
      return ""
   })))
   return x.filter((a) => a).map((a) => a.split(SEPERATOR))
}



function SavedMapLayerGroup(props: FastLagGroup<LayerInfo>) {

   if (props.customFeatureLayers === undefined || props.customFeatureLayers.length === 0) {
      return (<></>)
   }
   // props.editFeatureAvailable
   // props.callbackToConvertCustomFeatureLayerToNormal

   if (props.customFeatureLayers) {
      let customLayer = props.customFeatureLayers!


      let uniqueGroups = uniqueSubGroup(customLayer, []).filter((a) => a)

      return (
         <div className='GroupWrap'>
            {uniqueGroups.map(group =>
               <Fragment key={group.join()} >
                  <SavedMapLayerNester group={group} layersInGroup={FasteLagInGroup(group, customLayer)} onCustomFeatureLayerToggle={props.onCustomFeatureLayerToggle} />
               </Fragment>
            )}

         </div>
      )
   }
   return (<></>)
}

function SavedMapLayerNester(props: { group: string[], layersInGroup: LayerInfo[], onCustomFeatureLayerToggle }) {
   const [collapsed, setCollapsed] = useState(true)

   let activeInGroup = props.layersInGroup.filter((a) => a.visible)
   function toggleGroup(e) {
      e.preventDefault()
      e.stopPropagation();
      if (activeInGroup.length) {
         activeInGroup.forEach((a) => {
            props.onCustomFeatureLayerToggle(a)
         })
      } else {
         props.layersInGroup.forEach((a) => {
            props.onCustomFeatureLayerToggle(a)
         })
      }
   }

   if (collapsed) {
      return <div className='GroupWrap'>
         <div onClick={() => setCollapsed((a) => (!a))} className='GroupHead'>
            <GlassCheckbox onClick={toggleGroup} checked={!!activeInGroup.length} indeterminate={!!(activeInGroup.length && activeInGroup.length < props.layersInGroup.length || false)} />
            {props.group.at(-1) ?? props.group}<div style={{ marginLeft: "auto" }}>
               <SmallIconBtn ><MIBIcon icon="expandLayer" className='transformer' style={{ transform: "Rotate(" + (!collapsed ? "90deg" : "0deg") + ")" }} /></SmallIconBtn>
            </div></div></div>
   }

   let uniqueGroups = uniqueSubGroup(props.layersInGroup, props.group).filter((a) => a)


   return (<div className='GroupWrap'>
      <div onClick={() => setCollapsed((a) => (!a))} className='GroupHead'>
         <GlassCheckbox onClick={toggleGroup} checked={!!activeInGroup.length} indeterminate={!!(activeInGroup.length && activeInGroup.length < props.layersInGroup.length || false)} />
         {props.group.at(-1) ?? props.group}<div style={{ marginLeft: "auto" }}>
            <SmallIconBtn ><MIBIcon icon="expandLayer" className='transformer' style={{ transform: "Rotate(" + (!collapsed ? "90deg" : "0deg") + ")" }} /></SmallIconBtn>
         </div></div>

      <div className='layerInGroup' >

         {uniqueGroups.map(group =>
            <Fragment key={group.join()} >
               <SavedMapLayerNester group={group} layersInGroup={FasteLagInGroup(group, props.layersInGroup)} onCustomFeatureLayerToggle={props.onCustomFeatureLayerToggle} />
            </Fragment>
         )}
         <SavedMapLayer layers={FasteLagAtLevel(props.group, props.layersInGroup)} onCustomFeatureLayerToggle={props.onCustomFeatureLayerToggle} />
      </div></div>
   )

   // return (
   //    <div className='GroupWrap'>

   //    <span className='mit-transformer' style={{float:"right", transform:"Rotate("+(props.collapse?"90deg":"0deg")+")"}}><BsChevronRight /></span>
   //    </div></div>
   //    <div className='layerInGroup' >
   //    {props.children}
   //    </div></div>
   // )
}

function SavedMapLayer(props: { layers: LayerInfo[], onCustomFeatureLayerToggle }) {
   function toggleChilde(idx) {
      props.onCustomFeatureLayerToggle(props.layers[idx])
   }

   return (
      props.layers.map((layer, idx) => {
         // onClick={(evt, val) => layer.onChange(key, val)}
         return (<div key={layer.datasetname} className='layer'>
            <GlassCheckbox onClick={() => toggleChilde(idx)} checked={layer.visible} >{layer.datasetname}</GlassCheckbox>
         </div>)
      })
   )
}

export function FeatureLayers(props: any) {
   let [nestedTreeStruct, setNeststedTreeStruct] = useState({})

   useEffect(() => {
      let k = {}
      const getP = (a) => props.layers[a].layer.priority || 999;
      Object.keys(props.layers).filter((a) => (props.layers[a]?.layer?.group !== "hidden")).sort((a, b) => getP(a) - getP(b)).forEach(key => {
         let group = props.layers[key]?.layer.group;
         let path = group ? group.split(".") : ["Andet"];
         extendNestedGroups(k, path, props.layers[key], key)
      });
      setNeststedTreeStruct({ ...k });
   }, [props.layers])

   return (
      <>
         <FeatureLayersNester onChange={props.onChange} showSettings={props.showSettings} settingClick={props.settingClick} selectedFeatureLayerKeys={props.selectedFeatureLayerKeys} curPos={nestedTreeStruct} maxLayersToActivate={props.maxLayersToActivate} />
      </>
   )


}

type FeatureLayersNesterType = {
   onChange: (a, b, c, d) => void,
   settingClick: (a, b) => void,
   showSettings?: boolean,
   selectedFeatureLayerKeys: string[],
   curPos: { [data: string]: any },
   name?: string,
   maxLayersToActivate?: number
}
function FeatureLayersNester(props: FeatureLayersNesterType) {
   let [collapse, setCollapse] = useState(false)
   let curPos = props.curPos

   if (props.curPos === undefined || Object.keys(props.curPos).length < 1) {
      return (<></>);
   }

   function clickHandler(evt, key: string, a: string[], boolean) {
      evt.stopPropagation();
      evt.preventDefault();
      props.onChange(
         props.name || key, boolean, a.map((a) => ({ key: a })), boolean)
   }

   function settingClick(a: string, boolean) {
      props.settingClick?.(a, boolean)
   }

   let maxCheckedAtATime = props.maxLayersToActivate ?? SettingsManager.getSystemSetting("LayerListMaxLayertoActivate", 12, true);

   let layerInGroup = LayerInGroup(curPos);
   let activeInGroup = layerInGroup.filter((a) => props.selectedFeatureLayerKeys.find((b) => a === b))

   let buttonCheck = <GlassCheckbox disabled={activeInGroup.length === 0 && layerInGroup.length > maxCheckedAtATime} checked={!!activeInGroup.length} indeterminate={!!(activeInGroup.length && activeInGroup.length < layerInGroup.length || false)}
      title={activeInGroup.length === 0 && layerInGroup.length > maxCheckedAtATime ? Localization.getText("Too many layers to turn everything on") : undefined}
      onClick={(evt, val) => {
         ActionTracker.track("LayerControl", "FeatureLayerGroup", val ? "ToggleOn" : "ToggleOff", { groupID: props.name || "", groupLayers: layerInGroup.map((a) => a) });
         clickHandler(evt, props.name || "", layerInGroup, val)
      }}
   />

   function collapseHandler() {
      setCollapse((a) => !a);
   }

   return (
      <Wrapper name={props.name} collapseHandler={collapseHandler} buttonCheck={buttonCheck} collapse={collapse}>
         {collapse || props.name == undefined ? Object.keys(curPos).map((key) => {
            if (key.startsWith("_g_")) {
               return (
                  <Fragment key={key}>
                     <FeatureLayersNester settingClick={settingClick} name={key.slice(3)} showSettings={props.showSettings} onChange={props.onChange} selectedFeatureLayerKeys={props.selectedFeatureLayerKeys} curPos={curPos[key]} maxLayersToActivate={maxCheckedAtATime} />
                  </Fragment>
               )
            } else {
               return (
                  <div key={key} className='layer'>
                     <GlassCheckbox onClick={(evt, val) => props.onChange((props.curPos[key] as any)?.layer?.translationTable ? Localization.getTextSpecificTable(key, (props.curPos[key] as any).layer.translationTable) : key, val, [{ key: key }], true)} checked={activeInGroup.includes(key)} >{(props.curPos[key] as any)?.layer?.translationTable ? Localization.getTextSpecificTable(key, (props.curPos[key] as any).layer.translationTable) : key}</GlassCheckbox>
                     {(props.curPos[key] as any)?.layer?.infoLink ? <LayerInfoLink layerId={props.curPos[key]} href={(props.curPos[key] as any)?.layer?.infoLink || ""} /> : null}
                     {props.showSettings ? <SettingsButton onClick={() => settingClick(key, true)} /> : null}
                  </div>
               )
            }
         }) : <></>}
      </Wrapper>
   )
}


type WrapperType = {
   name?: string,
   collapse: boolean,
   collapseHandler: () => void,
   buttonCheck: JSX.Element,
   children: React.ReactNode
}
function Wrapper(props: WrapperType) {
   if (props.name) {
      return (
         <div className='GroupWrap'>
            <div onClick={props.collapseHandler} className='GroupHead'>{props.buttonCheck} {getFeatureLayerGroupTranslation(Localization.getlanguage() || "da", props.name)}<div style={{ marginLeft: "auto" }}>
               <SmallIconBtn >
                  <MIBIcon icon="expandLayer" className='transformer' style={{ transform: "Rotate(" + (props.collapse ? "90deg" : "0deg") + ")" }} />
               </SmallIconBtn>
            </div></div>
            <div className='layerInGroup' >
               {props.children}
            </div></div>
      )
   } else {
      return (
         <>{props.children}</>
      )
   }
}



// HelperFunction
function extendNestedGroups(nested: {}, path: string[], obj: any, label: string) {
   let curPos = nested;

   let len = path.length;
   for (let i = 0; i < len; i++) {
      if (curPos["_g_" + path[i]]) {
         curPos = curPos["_g_" + path[i]];
         continue;
      } else {
         curPos["_g_" + path[i]] = {};
         curPos = curPos["_g_" + path[i]];
      }
   }
   curPos[label] = obj
}

function LayerInGroup(nested: {}): string[] {
   let list: string[] = [];
   Object.keys(nested).map((key) => {
      if (key.startsWith("_g_")) {
         list = list.concat(LayerInGroup(nested[key]))
      } else {
         list.push(key)
      }
   })
   return list
}

function getActiveGroupUUIDs(selectedFeatureLayerKeys: string[]): string[] {
   const groupUUIDs = new Set<string>();

   selectedFeatureLayerKeys.forEach(key => {
      if (key.includes("\\")) {
         // Extract UUID from prefixed layer key: "\\uuid\\layerName"
         const uuid = key.split("\\")[1];
         if (uuid) {
            groupUUIDs.add(uuid);
         }
      }
   });

   return Array.from(groupUUIDs);
}

function SaveSelectedLayers(layers: string[], groupUUIDs: string[]) {
   MitStorage.setValue("MIT-SelectedLayers", layers);
   MitStorage.setValue("MIT-SelectedGroupUUIDs", groupUUIDs);
}

function LayerInfoLink(props: { layerId?: string, href?: string }) {
   return (
      <SmallIconBtn className="layerBtn shownOnHoverParent"
         onClick={(e) => {
            ActionTracker.track("LayerControl", "FeatureLayer", "OpenInfo", { layerID: props.layerId, href: props.href || "" });
            let { width: SW, height } = window.screen
            let width = Math.min(1300, SW * 0.9);
            let left = (SW - width) / 2;
            e.stopPropagation(); window.open(props.href || "", "_blank", `height=${height},width=${width},left=${left}`
            )
         }}
         title={Localization.getText("Open link to layer info")}
      >
         <MIBIcon icon="layerInfo"
         />
      </SmallIconBtn>
   );

}

function SettingsButton(props: { onClick: () => any }) {

   return (
      <SmallIconBtn className={"shownOnHoverParent"} onClick={() => {
         ActionTracker.track("LayerControl", "FeatureLayer", "OpenEditor", {});
         props.onClick()
      }}
         title={Localization.getText("Edit layer")}
      >
         <MIBIcon icon="layerSettings"
         /></SmallIconBtn>

   )
}

function CustomGroupVersion(obj: CustomGroup): CustomGroup {
   if ("version" in obj && typeof obj.version == "string" && obj.version) {
      switch (obj.version) {
         case "v1.0":
            delete obj.version
            return obj
         default:
            return obj
      }
   }
   return obj
}

function validateCustomGroup(obj: any): obj is CustomGroup {
   if (obj.version !== undefined) {

   }
   if (obj.uuid === undefined || obj.title === undefined || obj.bgLayer === undefined || obj.layers === undefined) {
      return false
   }
   return true
}

function downloadJson(Content: string, filename: string) {
   const a = document.createElement("a");
   const file = new Blob([Content], { type: "application/json" });
   a.href = URL.createObjectURL(file);
   a.download = filename;
   a.click();
   URL.revokeObjectURL(a.href);
   a.remove();
}
